import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { MapPin, Tag, Navigation, CreditCard, Radio, Globe, Sparkles, ArrowRight, Shield } from 'lucide-react';

const ECO_LOGO = 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/6e402osn_ECOparkingTechLogosFullColor.jpg';
const HERO_SCREENSHOT = 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/tofi3f13_Screenshot%202026-03-11%20at%2010.50.53%E2%80%AFAM.png';

const FEATURES = [
  {
    icon: MapPin,
    title: 'Real-Time Parking Availability',
    description: 'Display live availability across every lot, garage, and surface space in your entire parking inventory. Give parkers the information they need, when they need it.',
    color: '#10b981',
  },
  {
    icon: Tag,
    title: 'Custom Tags & Space Types',
    description: 'Create custom tags like ADA, EV Charging, Reserved, or Permit so parkers can instantly identify the type of parking available per lot or garage.',
    color: '#6366f1',
  },
  {
    icon: Navigation,
    title: 'Instant Navigation',
    description: 'Allow users to quickly navigate to their nearest available space with one tap. Integrated directions reduce time spent searching and circling.',
    color: '#3b82f6',
  },
  {
    icon: CreditCard,
    title: 'Reserve & Pay In Real-Time',
    badge: 'Pro',
    description: 'Connect with payment providers to let users reserve and pay for parking space in real-time. Reduce friction and increase revenue per space.',
    color: '#f59e0b',
  },
  {
    icon: Radio,
    title: 'Sensor-Driven Counts',
    description: 'Receive real-time occupancy counts powered by ECO Parking Technologies space detection sensors. Accurate, reliable, and always up to date.',
    color: '#ef4444',
  },
  {
    icon: Globe,
    title: 'Custom Branded Web Pages',
    description: 'Publish to a customizable domain URL and embed your brand — logos, colors, and organization name — for a seamless branded experience.',
    color: '#8b5cf6',
  },
  {
    icon: Sparkles,
    title: 'Seamless User Experience',
    description: 'Deliver a beautiful, responsive, and intuitive parking experience on any device. Designed for universities, cities, hospitals, and enterprise campuses.',
    color: '#0ea5e9',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ECO_LOGO} alt="ECO Parking Technologies" className="w-auto" style={{ height: '8rem', mixBlendMode: 'multiply' }} />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => window.open('https://www.ecoparkingtechnologies.com/talk-to-sales', '_blank')}
              className="text-sm text-slate-600 hover:text-slate-900"
              data-testid="nav-talk-to-sales"
            >
              Talk to Sales
            </Button>
            <Button
              onClick={() => navigate('/admin')}
              className="bg-slate-900 text-white hover:bg-slate-800 text-sm"
              data-testid="nav-admin-login"
            >
              Admin Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50" />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-16 lg:pt-16 lg:pb-20">
          {/* Text — compact */}
          <div className="max-w-2xl mx-auto text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold text-emerald-700 tracking-wide uppercase">Live Parking Intelligence</span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15]" data-testid="hero-title">
              Help your visitors know where to park.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500">Before they arrive.</span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-slate-600 max-w-lg mx-auto leading-relaxed">
              Give your parkers a branded, real-time parking map — powered by sensor data — so they can find, navigate to, and reserve available spaces across your entire inventory.
            </p>
            <div className="mt-6">
              <Button
                onClick={() => window.open('https://www.ecoparkingtechnologies.com/talk-to-sales', '_blank')}
                className="bg-slate-900 text-white hover:bg-slate-800 px-6 py-3 text-sm h-auto"
                data-testid="hero-cta"
              >
                Request a Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Screenshot — large */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200 ring-1 ring-black/5">
              <img
                src={HERO_SCREENSHOT}
                alt="ECO Parking Maps — Real-time availability with garage detail card showing Pay For Reserved Parking"
                className="w-full h-auto"
                data-testid="hero-screenshot"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-emerald-100 rounded-2xl -z-10 opacity-60" />
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-blue-100 rounded-2xl -z-10 opacity-60" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="text-center mb-16">
            <h2 className="text-base sm:text-lg font-bold text-slate-900" data-testid="features-heading">
              Everything you need to modernize parking guidance
            </h2>
            <p className="mt-3 text-sm text-slate-500 max-w-lg mx-auto">
              From real-time availability to mobile payments — a complete platform for parking operators and their customers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${f.color}12` }}
                  >
                    <f.icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-800">{f.title}</h3>
                      {f.badge && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                          {f.badge}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / CTA */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="bg-slate-900 rounded-3xl px-8 py-14 sm:px-14 text-center">
            <Shield className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-base sm:text-lg font-bold text-white">
              Ready to transform your parking experience?
            </h2>
            <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto leading-relaxed">
              Join universities, cities, hospitals, and enterprise campuses already using ECO Parking Maps to guide parkers in real time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Button
                onClick={() => window.open('https://www.ecoparkingtechnologies.com/talk-to-sales', '_blank')}
                className="bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 text-sm h-auto font-semibold"
                data-testid="cta-talk-to-sales"
              >
                Talk to Sales <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-6 py-3 text-sm h-auto"
              >
                Admin Login
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={ECO_LOGO} alt="ECO Parking Technologies" className="h-6 w-auto" />
            <span className="text-xs text-slate-400">&copy; {new Date().getFullYear()} ECO Parking Technologies. All rights reserved.</span>
          </div>
          <a
            href="https://www.ecoparkingtechnologies.com/talk-to-sales"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            www.ecoparkingtechnologies.com
          </a>
        </div>
      </footer>
    </div>
  );
}
